import React, { useState, useRef } from 'react';
import {
  Typography, Card, Table, Tag, Button, Space, Row, Col, Tooltip, Statistic, Alert,
  Badge, Modal, Input, message, Form, Select,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, DollarOutlined,
  WalletOutlined, ThunderboltOutlined, LinkOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { walletApi, settlementApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title, Text, Paragraph } = Typography;

const SettlementManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const rejectReasonRef = useRef('');
  const [withdrawalFilters, setWithdrawalFilters] = useState({
    search: '',
    status: undefined as string | undefined,
    request_source: undefined as string | undefined,
  });
  const [withdrawalPage, setWithdrawalPage] = useState({ current: 1, pageSize: 20 });
  const [withdrawalForm] = Form.useForm();

  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useQuery(['wallet-balance'], walletApi.getBalance, { refetchInterval: 60000 });
  const { data: transactions, isLoading: txLoading, refetch: refetchTx } = useQuery(['wallet-transactions'], () => walletApi.getTransactions({ limit: 20 }));
  const { data: walletStats, isLoading: statsLoading, refetch: refetchStats } = useQuery(['wallet-stats'], () => walletApi.getStats({ days: 30 }));

  // 待审核提现列表
  const { data: pendingData, isLoading: pendingLoading, refetch: refetchPending } = useQuery(
    ['pending-withdrawals'],
    () => settlementApi.getWithdrawals({ status: 'pending', limit: 50 }),
    { refetchInterval: 30000 }
  );
  const pendingList = (pendingData as any)?.items || [];
  const { data: withdrawalData, isLoading: withdrawalLoading, refetch: refetchWithdrawals } = useQuery(
    ['withdrawal-query', withdrawalFilters, withdrawalPage.current, withdrawalPage.pageSize],
    () =>
      settlementApi.getWithdrawals({
        status: withdrawalFilters.status,
        request_source: withdrawalFilters.request_source,
        search: withdrawalFilters.search || undefined,
        limit: withdrawalPage.pageSize,
        offset: (withdrawalPage.current - 1) * withdrawalPage.pageSize,
      }),
    { keepPreviousData: true }
  );
  const withdrawalList = (withdrawalData as any)?.items || [];
  const withdrawalTotal = (withdrawalData as any)?.total || 0;
  const withdrawalStats = (withdrawalData as any)?.stats || {};

  const approveMutation = useMutation({
    mutationFn: async (id: number) => {
      await settlementApi.approveWithdrawal(id);
      await settlementApi.executeTransfer(id);
    },
    onSuccess: () => {
      message.success('已批准并执行转账');
      refetchPending();
      refetchBalance();
      refetchTx();
      refetchStats();
      refetchWithdrawals();
    },
    onError: (err: any) => message.error(err?.response?.data?.detail || '操作失败'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      settlementApi.rejectWithdrawal(id, reason),
    onSuccess: () => {
      message.success('已拒绝，余额已退回');
      refetchPending();
      refetchStats();
      refetchWithdrawals();
    },
    onError: (err: any) => message.error(err?.response?.data?.detail || '操作失败'),
  });

  const handleApprove = (record: any) => {
    Modal.confirm({
      title: '确认批准并打款',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定批准此笔提现并自动执行链上转账？</p>
          <p>提现金额: <strong>{record.amount?.toFixed(2)} USDT</strong></p>
          <p>到账金额: <strong>{record.actual_amount?.toFixed(2)} USDT</strong></p>
          <p>地址: <code>{record.account_info_parsed?.address || '-'}</code></p>
        </div>
      ),
      okText: '确认打款',
      okType: 'primary',
      cancelText: '取消',
      onOk: () => approveMutation.mutateAsync(record.id),
    });
  };

  const handleReject = (record: any) => {
    rejectReasonRef.current = '';
    Modal.confirm({
      title: '拒绝提现',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>提现金额: <strong>{record.amount?.toFixed(2)} USDT</strong>，余额将退回用户账户。</p>
          <p style={{ marginTop: 8 }}>拒绝原因:</p>
          <Input.TextArea
            rows={2}
            placeholder="请输入拒绝原因"
            onChange={(e) => { rejectReasonRef.current = e.target.value; }}
          />
        </div>
      ),
      okText: '确认拒绝',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => rejectMutation.mutateAsync({ id: record.id, reason: rejectReasonRef.current || '管理员拒绝' }),
    });
  };

  const parseAccountInfo = (info: string) => {
    try { return JSON.parse(info.replace(/'/g, '"')); } catch { return {}; }
  };
  const getWithdrawSourceTag = (source?: string) => (
    source === 'auto'
      ? <Tag color="blue">自动提现</Tag>
      : <Tag color="default">手动提现</Tag>
  );
  const getWithdrawalStatusTag = (status: string) => {
    const m: any = {
      completed: { color: 'success', text: '已完成', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', text: '失败', icon: <CloseCircleOutlined /> },
      processing: { color: 'processing', text: '处理中' },
      pending: { color: 'warning', text: '待审核' },
      approved: { color: 'blue', text: '已批准' },
      rejected: { color: 'error', text: '已拒绝' },
      cancelled: { color: 'default', text: '已取消' },
    };
    const info = m[status] || { color: 'default', text: status || '-' };
    return <Tag color={info.color} icon={info.icon}>{info.text}</Tag>;
  };

  const pendingColumns = [
    {
      title: '用户', dataIndex: 'user_name', key: 'user_name', width: 120,
      render: (_: any, record: any) => (
        <Tooltip title={`Telegram ID: ${record.telegram_id || '-'}`}>
          <Text>{record.user_name || record.telegram_id || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '提现方式', dataIndex: 'request_source', key: 'request_source', width: 110,
      render: (source: string) => getWithdrawSourceTag(source),
    },
    {
      title: '提现金额(USDT)', dataIndex: 'amount', key: 'amount', width: 140,
      render: (v: number) => <Text strong>{v?.toFixed(2) || '0.00'} USDT</Text>,
    },
    {
      title: '到账USDT', dataIndex: 'actual_amount', key: 'actual_amount', width: 120,
      render: (v: number) => <Text strong style={{ color: '#52c41a' }}>{v?.toFixed(2) || '0.00'} USDT</Text>,
    },
    {
      title: '手续费', dataIndex: 'fee_usdt', key: 'fee_usdt', width: 100,
      render: (v: number) => `${(v ?? 0).toFixed(2)} USDT`,
    },
    {
      title: '收款地址', dataIndex: 'account_info', key: 'account_info', width: 150,
      render: (info: string) => {
        const parsed = parseAccountInfo(info || '{}');
        const addr = parsed.address || '';
        return <Tooltip title={addr}><Text code style={{ fontSize: 11 }}>{addr ? `${addr.slice(0, 6)}...${addr.slice(-6)}` : '-'}</Text></Tooltip>;
      },
    },
    {
      title: '时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (date: string) => date ? formatBeijingTime(date) : '-',
    },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button type="primary" size="small" onClick={() => handleApprove({ ...record, account_info_parsed: parseAccountInfo(record.account_info || '{}') })}
            loading={approveMutation.isLoading}>
            批准并打款
          </Button>
          <Button danger size="small" onClick={() => handleReject(record)}
            loading={rejectMutation.isLoading}>
            拒绝
          </Button>
        </Space>
      ),
    },
  ];

  const withdrawalColumns = [
    {
      title: '提现人', dataIndex: 'user_name', key: 'user_name', width: 140,
      render: (_: any, record: any) => (
        <Tooltip title={`Telegram ID: ${record.telegram_id || '-'}`}>
          <Text>{record.user_name || record.username || record.telegram_id || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '提现方式', dataIndex: 'request_source', key: 'request_source', width: 110,
      render: (source: string) => getWithdrawSourceTag(source),
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (status: string) => getWithdrawalStatusTag(status),
    },
    {
      title: '提现金额(USDT)', dataIndex: 'amount', key: 'amount', width: 140,
      render: (v: number) => <Text strong>{v?.toFixed(2) || '0.00'} USDT</Text>,
    },
    {
      title: '到账USDT', dataIndex: 'actual_amount', key: 'actual_amount', width: 120,
      render: (v: number) => <Text strong style={{ color: '#52c41a' }}>{v?.toFixed(2) || '0.00'} USDT</Text>,
    },
    {
      title: '手续费', dataIndex: 'fee_usdt', key: 'fee_usdt', width: 100,
      render: (v: number) => `${(v ?? 0).toFixed(2)} USDT`,
    },
    {
      title: '收款地址', dataIndex: 'account_info', key: 'account_info', width: 150,
      render: (info: string) => {
        const parsed = parseAccountInfo(info || '{}');
        const addr = parsed.address || '';
        return <Tooltip title={addr}><Text code style={{ fontSize: 11 }}>{addr ? `${addr.slice(0, 6)}...${addr.slice(-6)}` : '-'}</Text></Tooltip>;
      },
    },
    {
      title: '提现单号', dataIndex: 'withdrawal_number', key: 'withdrawal_number', width: 170,
      render: (num: string) => <Tooltip title={num}><Text code style={{ fontSize: 11 }}>{num ? `${num.slice(0, 8)}...${num.slice(-8)}` : '-'}</Text></Tooltip>,
    },
    {
      title: '交易哈希', dataIndex: 'transaction_id', key: 'transaction_id', width: 150,
      render: (hash: string) => (
        <Space>
          <Text code style={{ fontSize: 11 }}>{hash ? `${hash.slice(0, 6)}...${hash.slice(-6)}` : '-'}</Text>
          {hash && <Button type="link" size="small" icon={<LinkOutlined />} onClick={() => window.open(`https://tronscan.org/#/transaction/${hash}`, '_blank')} />}
        </Space>
      ),
    },
    {
      title: '申请时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (date: string) => date ? formatBeijingTime(date) : '-',
    },
  ];

  const balanceData = (balance as any) || {};
  const txData = (transactions as any) || [];
  const statsData = (walletStats as any) || {};

  const refreshAll = () => { refetchBalance(); refetchTx(); refetchStats(); refetchPending(); refetchWithdrawals(); message.success('正在刷新...'); };

  const getTrxStatus = () => {
    const trx = balanceData.trx_balance || 0;
    if (trx >= 100) return { status: 'success' as const, text: '充足' };
    if (trx >= 30) return { status: 'warning' as const, text: '偏低' };
    return { status: 'error' as const, text: '不足' };
  };
  const trxStatus = getTrxStatus();

  const txColumns = [
    {
      title: '交易哈希', dataIndex: 'tx_hash', key: 'tx_hash', width: 180,
      render: (hash: string) => (
        <Space>
          <Text code style={{ fontSize: 12 }}>{hash ? `${hash.slice(0, 8)}...${hash.slice(-8)}` : '-'}</Text>
          {hash && <Tooltip title="在TRONSCAN查看"><Button type="link" size="small" icon={<LinkOutlined />} onClick={() => window.open(`https://tronscan.org/#/transaction/${hash}`, '_blank')} /></Tooltip>}
        </Space>
      ),
    },
    {
      title: '提现人', dataIndex: 'user_name', key: 'user_name', width: 120,
      render: (name: string, record: any) => <Tooltip title={`Telegram ID: ${record.telegram_id || '-'}`}><Text>{name || '-'}</Text></Tooltip>,
    },
    {
      title: '转账金额', dataIndex: 'actual_amount', key: 'actual_amount', width: 120,
      render: (amount: number) => <Text strong style={{ color: '#52c41a' }}>{amount?.toFixed(2)} USDT</Text>,
    },
    {
      title: '手续费', dataIndex: 'fee_trx', key: 'fee_trx', width: 100,
      render: (fee: number) => <Text type="warning">{fee?.toFixed(2)} TRX</Text>,
    },
    {
      title: '能量消耗', dataIndex: 'energy_used', key: 'energy_used', width: 120,
      render: (energy: number) => <Text type="secondary">{energy?.toLocaleString()} Energy</Text>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (status: string) => {
        const m: any = {
          completed: { color: 'success', text: '成功', icon: <CheckCircleOutlined /> },
          failed: { color: 'error', text: '失败', icon: <CloseCircleOutlined /> },
          processing: { color: 'processing', text: '处理中' },
          pending: { color: 'default', text: '待处理' },
        };
        const info = m[status] || { color: 'default', text: status };
        return <Tag color={info.color} icon={info.icon}>{info.text}</Tag>;
      },
    },
    {
      title: '提现方式', dataIndex: 'request_source', key: 'request_source', width: 110,
      render: (source: string) => getWithdrawSourceTag(source),
    },
    {
      title: '收款地址', dataIndex: 'to_address', key: 'to_address', width: 150,
      render: (addr: string) => <Tooltip title={addr}><Text code style={{ fontSize: 11 }}>{addr ? `${addr.slice(0, 6)}...${addr.slice(-6)}` : '-'}</Text></Tooltip>,
    },
    {
      title: '时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (date: string) => date ? formatBeijingTime(date) : '-',
    },
  ];

  return (
    <div className="space-y-6">
      <Title level={2}>资金管理</Title>

      {/* 待审核提现 */}
      {pendingList.length > 0 && (
        <Card
          title={
            <Space>
              <Badge count={pendingList.length} offset={[10, 0]}>
                <span>待审核提现</span>
              </Badge>
            </Space>
          }
          size="small"
          extra={<Button icon={<ReloadOutlined />} onClick={() => refetchPending()} loading={pendingLoading} size="small">刷新</Button>}
        >
          <Table columns={pendingColumns} dataSource={pendingList} rowKey="id" loading={pendingLoading}
            pagination={false} scroll={{ x: 1000 }} size="small" />
        </Card>
      )}

      {/* 钱包地址 */}
      <Card size="small">
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            <Text type="secondary">钱包地址：</Text>
            <Paragraph copyable={{ text: balanceData.wallet_address }} style={{ display: 'inline', marginLeft: 8 }}>
              <Text code strong style={{ fontSize: 14 }}>{balanceData.wallet_address || '未配置'}</Text>
            </Paragraph>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<LinkOutlined />}
                onClick={() => window.open(`https://tronscan.org/#/address/${balanceData.wallet_address}`, '_blank')}
                disabled={!balanceData.wallet_address}>在TRONSCAN查看</Button>
              <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={balanceLoading || txLoading || statsLoading}>刷新</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 余额卡片 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic title="TRX 余额" value={balanceData.trx_balance || 0} precision={2} suffix="TRX"
              prefix={<ThunderboltOutlined style={{ color: '#faad14' }} />} loading={balanceLoading} />
            <div style={{ marginTop: 8 }}><Tag color={trxStatus.status}>{trxStatus.text}</Tag><Text type="secondary" style={{ fontSize: 12 }}>（转账手续费）</Text></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic title="USDT 余额" value={balanceData.usdt_balance || 0} precision={2} suffix="USDT"
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />} loading={balanceLoading} />
            <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>可用于商户提现</Text></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic title="TronCastle 余额" value={balanceData.troncastle_balance ?? '-'} precision={2} suffix="TRX"
              prefix={<WalletOutlined style={{ color: '#1890ff' }} />} loading={balanceLoading} />
            <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>能量租赁账户余额</Text></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic title="平均手续费" value={statsData.avg_fee_trx || 0} precision={2} suffix="TRX/笔"
              prefix={<ThunderboltOutlined style={{ color: '#722ed1' }} />} loading={statsLoading} />
            <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>近30天平均</Text></div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card size="small">
            <Statistic
              title="商铺余额总计"
              value={statsData.merchant_balance_total_cny || 0}
              precision={2}
              suffix="USDT"
              prefix={<WalletOutlined style={{ color: '#13c2c2' }} />}
              loading={statsLoading}
            />
            <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>全站商户余额汇总</Text></div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small">
            <Statistic
              title="当前可提现余额总计"
              value={statsData.withdrawable_balance_total_cny || 0}
              precision={2}
              suffix="USDT"
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
              loading={statsLoading}
            />
            <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>已扣除 24 小时内待入账金额</Text></div>
          </Card>
        </Col>
      </Row>

      {/* 低余额警告 */}
      {balanceData.trx_balance < 30 && balanceData.wallet_address && (
        <Alert message="TRX余额不足"
          description={`当前TRX余额仅剩 ${balanceData.trx_balance?.toFixed(2)} TRX，可能无法支持足够的提现转账。建议充值 TRX 到热钱包地址。`}
          type="warning" showIcon />
      )}

      {/* 提现查询 */}
      <Card title="提现查询" size="small">
        <Form
          form={withdrawalForm}
          layout="inline"
          initialValues={withdrawalFilters}
          onFinish={(values) => {
            setWithdrawalPage((prev) => ({ ...prev, current: 1 }));
            setWithdrawalFilters({
              search: values.search?.trim() || '',
              status: values.status,
              request_source: values.request_source,
            });
          }}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="search" label="提现人">
            <Input allowClear placeholder="姓名/@用户名/Telegram ID/单号" style={{ width: 260 }} />
          </Form.Item>
          <Form.Item name="request_source" label="提现方式">
            <Select
              allowClear
              placeholder="全部"
              style={{ width: 140 }}
              options={[
                { label: '自动提现', value: 'auto' },
                { label: '手动提现', value: 'manual' },
              ]}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              allowClear
              placeholder="全部"
              style={{ width: 140 }}
              options={[
                { label: '待审核', value: 'pending' },
                { label: '处理中', value: 'processing' },
                { label: '已完成', value: 'completed' },
                { label: '已拒绝', value: 'rejected' },
                { label: '失败', value: 'failed' },
                { label: '已取消', value: 'cancelled' },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">查询</Button>
              <Button
                onClick={() => {
                  withdrawalForm.resetFields();
                  setWithdrawalFilters({ search: '', status: undefined, request_source: undefined });
                  setWithdrawalPage({ current: 1, pageSize: 20 });
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Table
          columns={withdrawalColumns}
          dataSource={withdrawalList}
          rowKey="id"
          loading={withdrawalLoading}
          pagination={{
            current: withdrawalPage.current,
            pageSize: withdrawalPage.pageSize,
            total: withdrawalTotal,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (current, pageSize) => setWithdrawalPage({ current, pageSize }),
          }}
          scroll={{ x: 1320 }}
          size="small"
        />

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={12} md={6}>
            <Statistic title="筛选总笔数" value={withdrawalStats.total_count || 0} suffix="笔" />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="筛选总金额" value={withdrawalStats.total_amount_usdt ?? withdrawalStats.total_amount_cny ?? 0} precision={2} suffix="USDT" />
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="自动提现" value={withdrawalStats.auto_count || 0} suffix="笔" valueStyle={{ color: '#1677ff' }} />
            <Text type="secondary">{(withdrawalStats.auto_amount_usdt ?? withdrawalStats.auto_amount_cny ?? 0).toFixed(2)} USDT</Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="手动提现" value={withdrawalStats.manual_count || 0} suffix="笔" />
            <Text type="secondary">{(withdrawalStats.manual_amount_usdt ?? withdrawalStats.manual_amount_cny ?? 0).toFixed(2)} USDT</Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="待审核" value={withdrawalStats.pending_count || 0} suffix="笔" valueStyle={{ color: '#faad14' }} />
            <Text type="secondary">{(withdrawalStats.pending_amount_usdt ?? withdrawalStats.pending_amount_cny ?? 0).toFixed(2)} USDT</Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="已完成" value={withdrawalStats.completed_count || 0} suffix="笔" valueStyle={{ color: '#52c41a' }} />
            <Text type="secondary">{(withdrawalStats.completed_amount_usdt ?? withdrawalStats.completed_amount_cny ?? 0).toFixed(2)} USDT</Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="已拒绝" value={withdrawalStats.rejected_count || 0} suffix="笔" valueStyle={{ color: '#ff4d4f' }} />
            <Text type="secondary">{(withdrawalStats.rejected_amount_usdt ?? withdrawalStats.rejected_amount_cny ?? 0).toFixed(2)} USDT</Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="失败/取消" value={withdrawalStats.failed_count || 0} suffix="笔" valueStyle={{ color: '#8c8c8c' }} />
            <Text type="secondary">{(withdrawalStats.failed_amount_usdt ?? withdrawalStats.failed_amount_cny ?? 0).toFixed(2)} USDT</Text>
          </Col>
        </Row>
      </Card>

      {/* 30天统计 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="30天提现统计" size="small" loading={statsLoading}>
            <Space direction="vertical" className="w-full" size="middle">
              <div className="flex justify-between"><Text>总提现笔数</Text><Text strong>{statsData.total_withdrawals || 0} 笔</Text></div>
              <div className="flex justify-between"><Text>成功</Text><Text strong style={{ color: '#52c41a' }}>{statsData.completed_withdrawals || 0} 笔</Text></div>
              <div className="flex justify-between"><Text>失败</Text><Text strong style={{ color: '#ff4d4f' }}>{statsData.failed_withdrawals || 0} 笔</Text></div>
              <div className="flex justify-between"><Text>成功率</Text>
                <Text strong>{statsData.total_withdrawals > 0 ? ((statsData.completed_withdrawals / statsData.total_withdrawals) * 100).toFixed(1) : 0}%</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="30天金额统计" size="small" loading={statsLoading}>
            <Space direction="vertical" className="w-full" size="middle">
              <div className="flex justify-between"><Text>总转出金额</Text><Text strong style={{ color: '#52c41a' }}>{statsData.total_amount?.toFixed(2) || '0.00'} USDT</Text></div>
              <div className="flex justify-between"><Text>总手续费</Text><Text strong style={{ color: '#faad14' }}>{statsData.total_fee_trx?.toFixed(2) || '0.00'} TRX</Text></div>
              <div className="flex justify-between"><Text>平均每笔手续费</Text><Text strong>{statsData.avg_fee_trx?.toFixed(2) || '0.00'} TRX</Text></div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 链上交易记录 */}
      <Card title="链上交易记录" size="small">
        <Table columns={txColumns} dataSource={txData} rowKey="tx_hash" loading={txLoading}
          pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true, showTotal: (total) => `共 ${total} 条记录` }}
          scroll={{ x: 1000 }} size="small" />
      </Card>
    </div>
  );
};

export default SettlementManagement;
